import React from 'react'
import { useParams } from 'react-router-dom'

import { Box, Fade, Stack } from '@mui/material'

import { Card, Skeleton } from '@repo/ui'
import { Header, QueryErrorState, STATUS_TOKEN_H } from 'components'
import { useSnackbar } from 'contexts'
import { DEFERRED_LOADING_FADE_MS, useDeferredLoading } from 'hooks'
import { useReplyToSupportRequest, useSupportRequest } from 'hooks/api'

import { PROSE_LINE_H, ProseSkeleton } from '../ProseSkeleton'
import { BoardItemLink } from './BoardItemLink'
import { RequestThread } from './RequestThread'

/**
 * Readable-measure cap, matching the settings pages' NARROW_COLUMN_WIDTH.
 *
 * A thread is prose — one paragraph per message. At full width on a wide display a reply ran the
 * better part of a metre, which is the same reason Appearance and the two request LISTS are capped.
 */
const REQUEST_COLUMN_WIDTH = 1100

/**
 * A human title for a request, derived from its body.
 *
 * ## Why derived rather than a field
 *
 * The header read "Request #1011", which names nothing: you cannot tell one request from another in
 * a browser tab, a back-link or a breadcrumb, and the two status chips beside a bare number have
 * nothing to anchor to. A title fixes all of that.
 *
 * But the intake documents at length that it asks for NO subject — "a title is a second thing to
 * write before you can ask for help" — and that restraint is the feature. Deriving the title from
 * the body's first sentence gets the entire benefit with none of the cost: every request that
 * already exists gets a title with no migration, and nobody has to type one.
 *
 * If an explicit, user-authored title is wanted later, this function is the seam: add the column,
 * and fall back to this when it is empty. Nothing else has to change.
 */
export const requestTitle = (body: string): string => {
  const flat = (body ?? '').replace(/\s+/g, ' ').trim()
  if (!flat) return 'Untitled request'

  // First sentence, if there is a short one. A body often opens with a summary line and then
  // explains — that opening line IS the title the user would have written.
  const sentence = flat.split(/(?<=[.!?])\s/)[0]
  const candidate = sentence.length > 0 && sentence.length <= 80 ? sentence : flat

  // Trim the trailing full stop: a heading does not take one. Question and exclamation marks stay,
  // because they carry meaning a period does not.
  const trimmed = candidate.replace(/\.$/, '')
  return trimmed.length > 80 ? `${trimmed.slice(0, 79).trimEnd()}…` : trimmed
}


/**
 * One request and its conversation (VRG-226 — C2).
 *
 * The reply box is always available, including on a resolved or closed request.
 * That is intentional: the API reopens a resolved request when the customer
 * replies, so a follow-up cannot land in a thread nobody is watching. Hiding the
 * box on a closed request would push the user back to raising a duplicate.
 *
 * The box itself is `ReplyComposer`, rendered by `RequestThread` rather than by this
 * page, and it takes ATTACHMENTS as well as text. Both facts are deliberate: a reply is
 * part of the conversation, so it belongs to the thread component the internal queue
 * also renders; and "can you send a screenshot?" is the most common thing a support
 * agent types, which until now had no answer on this surface.
 */
export const SupportRequestPage = () => {
  const { id } = useParams<{ id: string }>()
  const { open: notification } = useSnackbar()

  const { data: request, isLoading, isError, error, refetch } = useSupportRequest(id)
  const { mutateAsync: reply, isPending } = useReplyToSupportRequest(id ?? '')

  /**
   * Nothing to show yet — `!request` included, for the reason spelled out at the skeleton
   * branch below: a query that has settled without data must keep the skeleton, not fall
   * through to a page rendered against `undefined`.
   */
  const pending = isLoading || !request

  /**
   * Whether the skeleton may be on screen. Deferred so a request that loads in ~30ms — the
   * normal case locally — never mounts one at all. See `useDeferredLoading`.
   */
  const showSkeleton = useDeferredLoading(pending)

  /**
   * Post the reply, and let the composer decide what to do next.
   *
   * `mutateAsync` rather than `mutate` + callbacks because `ReplyComposer` clears itself
   * on the resolved promise and keeps the draft on a rejection — which needs the outcome
   * as a value, not as a callback. The `throw` after notifying is what carries the
   * failure back to it; swallowing here would clear a reply that never sent.
   */
  const handleReply = (payload: { body: string; attachmentUrls: string[] }) =>
    reply(payload).catch((error: any) => {
      notification({
        type: 'error',
        title: error?.message || "Couldn't post your reply. Please try again.",
      })
      throw error
    })

  // Same trap as the list surfaces, in a different disguise: `isLoading || !request`
  // is true forever on a 403 or a 404, so the page would sit on its skeleton
  // indefinitely rather than saying what went wrong. A permanent skeleton is at
  // least visibly broken, unlike an empty state, but it still tells the user
  // nothing they can act on.
  if (isError) {
    return (
      <Box sx={{ maxWidth: REQUEST_COLUMN_WIDTH, mx: 'auto' }}>
        <Header title="Request" breadcrumbsOveride="Requests" />
        <QueryErrorState
          error={error}
          subject="this request"
          permissionLabel="View company requests"
          onRetry={() => refetch()}
        />
      </Box>
    )
  }

  if (showSkeleton) {
    return (
      <Box sx={{ maxWidth: REQUEST_COLUMN_WIDTH, mx: 'auto' }}>
        {/*
          BARS, not the words "Request" / "Requests", and a SUBTITLE bar as well as a title one.

          The subtitle is the load-bearing part. `Header` gives the title row `minHeight: 49`,
          which a lone title (31.2px) sits inside without changing — but the loaded page passes a
          subtitle too, and title + 4px gap + 18px subtitle = 53.2 breaks past that floor. A
          skeleton with no subtitle therefore rendered a 49px title row against the loaded page's
          53.2, and every region below started 4.2px high and slid down on load. Same class of
          bug the `breadcrumbsOveride` note in `HeaderProps` describes, four pixels instead of
          twenty-five.

          Heights are the loaded elements' own: 31.2 is the h5 title's line box (24px × 130%) and
          18 is `body3`'s.
        */}
        <Header
          title={<Skeleton width={360} height={31.2} />}
          subtitle={<Skeleton width={96} height={18} />}
          breadcrumbsOveride={<Skeleton width={48} height={20} />}
        />
        {/*
          Mirrors the loaded layout region for region: the opening card, then the reply box.

          NO placeholder reply cards. There were two, and they were the largest single source of
          shift on this page — 216px of invented thread that evaporated on load. Two is not the
          common case and is not close to it: of the 14 requests in the database 10 have no
          messages at all, the median is 0 and the mean is 0.57. A request's message count cannot
          be known before its response arrives, so the only defensible choice is the mode, and the
          mode is none. A busy thread still pushes the reply box down — that is unavoidable — but
          the usual case now lands exactly where the skeleton put it.
        */}
        <Stack spacing={4}>
          <Card sx={{ p: 4, mb: 0 }}>
            <Stack spacing={3}>
              {/* Type badge, status pill, reference, then the date hard right — the loaded row's
                  own order. Both tokens are STATUS_TOKEN_H so the row is 28 tall, not the 22 it
                  used to be; the missing 6px landed on every region below it. */}
              <Stack direction="row" spacing={3} alignItems="center">
                <Skeleton width={68} height={STATUS_TOKEN_H} />
                <Skeleton width={56} height={STATUS_TOKEN_H} />
                <Skeleton width={36} height={18} />
                <Box sx={{ flex: 1 }} />
                <Skeleton width={138} height={18} />
              </Stack>
              {/* One line. Every request body in the database renders on one line at this
                  column width (longest is 107 characters against a ~125-character measure), so
                  one is both the median and the mode. Revisit with a re-measure if bodies get
                  longer — each extra rendered line is 21px of shift. */}
              <ProseSkeleton lines={1} />
              {/* The auto-captured context block: `pt: 2` then a `body4` line each for the page
                  URL and the browser. Two lines is the mode — the user agent is recorded on all
                  14 requests and the page URL on 9. ("From:" is hidden when you are reading your
                  own request, which is the case this skeleton is on screen for.) */}
              <Box sx={{ pt: 2 }}>
                <ProseSkeleton lines={2} lineHeight={PROSE_LINE_H.body4} gap={4} />
              </Box>
            </Stack>
          </Card>
          {/* The reply box, mirrored control for control rather than as one block: an 18px
              field label, the 8px gap `FieldLabel` owns, the 106px four-row textarea (four
              lines at the composer's 14px/20.125px plus its 12px inset — see
              `MESSAGE_FIELD_SX`), then
              the SAME pair again for the attachments dropzone (its own label and its 140px
              box — see `ReplyComposer`), then the 40px send button on its own right-aligned
              row. The dropzone is the newest region and the largest: leaving it out of the
              skeleton would put 166px of shift under the textarea on every load, which is
              exactly the failure §19 names. */}
          <Card sx={{ p: 4, mb: 0 }}>
            <Stack spacing={3}>
              <Box>
                <Skeleton width={36} height={18} />
                <Skeleton height={106} sx={{ mt: 2 }} />
              </Box>
              <Box>
                <Skeleton width={134} height={18} />
                <Skeleton height={140} sx={{ mt: 2 }} />
              </Box>
              <Stack direction="row" justifyContent="flex-end">
                <Skeleton width={108} height={40} />
              </Stack>
            </Stack>
          </Card>
        </Stack>
      </Box>
    )
  }

  /*
    Still loading, but inside the skeleton's grace period. Render nothing rather than falling
    through — everything below dereferences `request`, and a page drawn against a half-second-old
    `undefined` is not a lighter version of this one, it is a crash.
  */
  if (pending) return null

  return (
    /* The fade wraps the page's OWN root rather than adding a wrapper, so it introduces no DOM
       and cannot change the layout it is decorating. Opacity only — the skeleton above is
       footprint-matched to this, so there is no movement here to disguise, and a transform
       would create some. It covers the header too, because the skeleton's header is bars: the
       whole page is what gets replaced, so the whole page is what fades. */
    <Fade in timeout={DEFERRED_LOADING_FADE_MS}>
      {/* Centred readable column, matching Appearance and the two request lists. Applied to the
          whole page including the header, so the title stays over its own content rather than
          floating left of it. */}
      <Box sx={{ maxWidth: REQUEST_COLUMN_WIDTH, mx: 'auto' }}>
        <Header
          title={requestTitle(request.body)}
          // The reference as an identifier under the title, not as the title. The chips that sit
          // beside it now have words to anchor to rather than a bare number.
          subtitle={`Request #${request.reference}`}
          // `breadcrumbsOveride` replaces the LEAF segment, so this makes the trail read
          // "Support / #1013" instead of "Support / Support" — the leaf was falling back to the
          // path and repeating its own parent.
          //
          // The parent still says "Support" rather than "Requests": that segment is derived from
          // the route (`/support/:id`) inside the shared Breadcrumbs, which has no per-segment
          // label map. Renaming it needs that map, which is a shared-component change worth making
          // deliberately rather than as a side effect of this page.
          breadcrumbsOveride={`#${request.reference}`}
        />

        <Stack spacing={4}>
          {/* Rendered above the thread, not buried under it: "other people asked for this
              too" is context for reading the thread rather than a footnote to it. The
              component renders nothing below a count of 2, so most requests show nothing —
              see BoardItemLink for why it stopped being a link when the board was retired
              (VRG-324). */}
          <BoardItemLink requestCount={request.boardRequestCount} />

          {/* `preferBoardStatus`: this is the CUSTOMER's view of their request, so
              a promoted feature shows the board status the list showed — the two
              must not disagree about the row the user just clicked. The internal
              queue renders this same component without the flag, keeping the
              request status a staffer acts on. */}
          {/* The composer is the thread's LAST element, handed in rather than rendered
              here: it belongs to the conversation, and putting it inside the thread is
              what lets the internal queue adopt the identical control (§36 — see
              `ReplyComposer`). */}
          <RequestThread
            request={request}
            preferBoardStatus
            reply={{
              label: 'Reply',
              placeholder: 'Add anything else that might help…',
              onSend: handleReply,
              isPending,
            }}
          />
        </Stack>
      </Box>
    </Fade>
  )
}
